import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { OutpostEvent, OutpostEventListResponse } from '../../../../../../../typings/outpost';
import Chip from '../../../common/base/Chip';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import DisplayDate from '../../../common/DisplayDate';
import { EmptyResults, StyledEmptyStateWrapper } from '../../../common/EmptyState';
import { Div } from '../../../common/helpers/StyledUtils';
import JSONBrowser, { DynamicColumnHeader } from '../../../common/JSONBrowser';
import Table from '../../../common/Table/Table';
import { parseDynamicColumn, TableContext } from '../../../contexts/TableContext';
import { getOutpostTenantLabel, useOutpostTenantsById } from '../utils/tenantDisplay';
const getNestedValue = (obj: any, path: (string | number)[]): any => {
  if (path.length === 0) return obj;
  const [first, ...rest] = path;
  if (!obj || typeof obj !== 'object') return undefined;
  return getNestedValue(obj[first], rest);
};

// Module-scope helper: the try/catch lives here (outside the component) so the React
// Compiler can memoize EventsList — it bails on any component whose body contains a
// try/catch statement.
const renderDynamicColumnField = (column_string: string, event: OutpostEvent) => {
  try {
    const parsed = parseDynamicColumn(column_string);
    let data: any;
    if (parsed.root === 'data') {
      data = event.data || {};
    } else if (parsed.root === 'metadata') {
      data = event.metadata || {};
    } else {
      return <Text muted size="xs">
          -
        </Text>;
    }
    const value = getNestedValue(data, parsed.path);
    const value_string = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (value === undefined || value === null) {
      return <Text muted size="xs">
          -
        </Text>;
    }
    return <Tooltip side="bottom" fill disabled={value_string.length <= 25} delay={500} element={<Div style={{
      maxHeight: '300px',
      width: '300px',
      overflow: 'auto'
    }} p={{
      x: 1
    }}>
            <Text size="xs" semi muted m={{
        b: 1
      }}>
              Payload
            </Text>
            <JSONBrowser json={value} compact />
          </Div>}>
        <Text mono size="xs" ellipsis>
          {value_string}
        </Text>
      </Tooltip>;
  } catch {
    return <Text muted size="xs">
        -
      </Text>;
  }
};
const BASE_WIDTHS = [{
  min: 152,
  max: 152
}, {
  min: 200
}, {
  min: 160
}];
const DYNAMIC_COL_WIDTH = {
  min: 200,
  max: 200
};
const EventsList: React.FC<{
  events_response: OutpostEventListResponse | undefined;
  loading: boolean;
  selected_event_id?: string;
  onEventSelected: (event_id: string | null) => void;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  current_page: number;
}> = t0 => {
  const $ = _c(43);
  const {
    events_response,
    loading,
    selected_event_id,
    onEventSelected,
    onNextPage,
    onPreviousPage,
    current_page
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["Time", "Tenant", "Topic"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const base_headers = t1;
  const {
    dynamic_columns,
    reorderDynamicColumns,
    updateDynamicColumnWidth
  } = useContext(TableContext);
  let t2;
  if ($[1] !== events_response?.models) {
    t2 = events_response?.models || [];
    $[1] = events_response?.models;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const events = t2;
  let t3;
  if ($[3] !== events) {
    t3 = events.map(_temp);
    $[3] = events;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    tenants_by_id
  } = useOutpostTenantsById(t3);
  let t4;
  if ($[5] !== dynamic_columns) {
    t4 = [...BASE_WIDTHS, ...dynamic_columns.map(_temp2)];
    $[5] = dynamic_columns;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const widths = t4;
  let t5;
  if ($[7] !== dynamic_columns) {
    t5 = [...base_headers, ...dynamic_columns.map(_temp3)];
    $[7] = dynamic_columns;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const headers = t5;
  let t6;
  bb0: {
    if (dynamic_columns.length === 0) {
      let t7;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = [];
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      t6 = t7;
      break bb0;
    }
    let t7;
    if ($[10] !== dynamic_columns.length) {
      let t8;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = (_, i) => i + base_headers.length;
        $[12] = t8;
      } else {
        t8 = $[12];
      }
      t7 = Array.from({
        length: dynamic_columns.length
      }, t8);
      $[10] = dynamic_columns.length;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t6 = t7;
  }
  const draggable_column_indices = t6;
  let t7;
  if ($[13] !== onEventSelected || $[14] !== selected_event_id) {
    t7 = id => {
      if (!id || selected_event_id === id) {
        onEventSelected(null);
      } else {
        onEventSelected(id);
      }
    };
    $[13] = onEventSelected;
    $[14] = selected_event_id;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const onRowSelected = t7;
  let t8;
  if ($[16] !== dynamic_columns || $[17] !== events || $[18] !== loading || $[19] !== selected_event_id || $[20] !== tenants_by_id) {
    t8 = loading ? [] : events.map(event_0 => {
      const dynamic_fields = dynamic_columns.map(column_string_1 => renderDynamicColumnField(column_string_1, event_0));
      const base_fields = [<DisplayDate key={`${event_0.id}-time`} date={event_0.time} show_timezone={false} />, <Chip outline={true} label={getOutpostTenantLabel(event_0.tenant_id, tenants_by_id[event_0.tenant_id])} to={`/tenants/${event_0.tenant_id}`} key={`${event_0.id}-tenant`} mono={true} />, <Text key={`${event_0.id}-topic`} mono={true} size="s" ellipsis={true}>{event_0.topic}</Text>];
      return {
        id: event_0.id,
        selected: selected_event_id === event_0.id,
        highlighted: selected_event_id === event_0.id,
        fields: [...base_fields, ...dynamic_fields]
      };
    });
    $[16] = dynamic_columns;
    $[17] = events;
    $[18] = loading;
    $[19] = selected_event_id;
    $[20] = tenants_by_id;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const rows = t8;
  if (events.length === 0 && !loading) {
    let t9;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <StyledEmptyStateWrapper><EmptyResults label="No matching events found" /></StyledEmptyStateWrapper>;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    return t9;
  }
  let t9;
  if ($[23] !== reorderDynamicColumns) {
    t9 = (active_index, over_index) => {
      reorderDynamicColumns(active_index - base_headers.length, over_index - base_headers.length);
    };
    $[23] = reorderDynamicColumns;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleColumnReorder = t9;
  let t10;
  if ($[25] !== dynamic_columns || $[26] !== updateDynamicColumnWidth) {
    t10 = (visual_index, new_width) => {
      const dynamic_index = visual_index - base_headers.length;
      if (dynamic_index >= 0 && dynamic_index < dynamic_columns.length) {
        const field_path = dynamic_columns[dynamic_index].split(":")[0];
        updateDynamicColumnWidth(field_path, new_width);
      }
    };
    $[25] = dynamic_columns;
    $[26] = updateDynamicColumnWidth;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleColumnResize = t10;
  const t11 = rows as any;
  let t12;
  if ($[28] !== current_page) {
    t12 = {
      current_page,
      page_size: 50,
      entity_name: "events"
    };
    $[28] = current_page;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = events_response?.models.length;
  let t14;
  if ($[30] !== draggable_column_indices || $[31] !== handleColumnReorder || $[32] !== handleColumnResize || $[33] !== headers || $[34] !== loading || $[35] !== onNextPage || $[36] !== onPreviousPage || $[37] !== onRowSelected || $[38] !== t11 || $[39] !== t12 || $[40] !== t13 || $[41] !== widths) {
    t14 = <Table fill={true} footer={true} top_offset={0} headers={headers} widths={widths} rows={t11} loading={loading} loading_title="Loading events" loading_message="Loading events..." onRowSelected={onRowSelected} draggable_columns={true} draggable_column_indices={draggable_column_indices} onColumnReorder={handleColumnReorder} onColumnResize={handleColumnResize} pagination={t12} onNextPage={onNextPage} onPreviousPage={onPreviousPage} current_count={t13} />;
    $[30] = draggable_column_indices;
    $[31] = handleColumnReorder;
    $[32] = handleColumnResize;
    $[33] = headers;
    $[34] = loading;
    $[35] = onNextPage;
    $[36] = onPreviousPage;
    $[37] = onRowSelected;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = widths;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
};
export default EventsList;
function _temp(event) {
  return event.tenant_id;
}
function _temp2(column_string) {
  const parsed = parseDynamicColumn(column_string);
  if (parsed.width) {
    return {
      min: parsed.width,
      max: parsed.width
    };
  }
  return DYNAMIC_COL_WIDTH;
}
function _temp3(column_string_0) {
  const parsed_0 = parseDynamicColumn(column_string_0);
  return <DynamicColumnHeader key={column_string_0} full_path={parsed_0.display_name} width={parsed_0.width} />;
}